
import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-hot-toast";
import FileUpload from "../../components/common/FileUpload";
import { DataGrid, GridColumnMenu, GridToolbar } from "@mui/x-data-grid";
import Loader from "../../components/common/Loader";
import Badge from "@mui/material/Badge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faMagnifyingGlass,
  faUserGraduate,
  faEnvelope,
  faUserCheck,
  faIdBadge,
  faEllipsisVertical,
  faDownload,
  faUpload,
} from "@fortawesome/free-solid-svg-icons";
import SecCard from "../../components/common/SecCard";
import { Row, Col } from "react-bootstrap";
import { green, yellow, red } from "@mui/material/colors";
import { SERVER_ORIGIN } from "../../utilities/constants";
import {
  hasReadOnlyAccess,
  getDashboardPath,
} from "../../utilities/helper_functions";
import HeaderCard from "../../components/common/HeaderCard";
import BackButton from "../../components/common/BackButton";
import YuvaEcAssignmentModal from "../../components/common/YuvaEcAssignmentModal";
import css from "../../css/admin/users-page.module.css";
import DownloadExcel from "../../components/common/DownloadExcel";
import AsyncSelect from "react-select/async";

const AdminUsers = () => {
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");
  const isECFromUrl = searchParams.get("isEC");

  const [activeTab, setActiveTab] = useState(tabFromUrl || "institute");
  const [isLoading, setIsLoading] = useState(false);
  const [limit, setLimit] = useState(0);
  const [allUsers, setAllUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [sortType, setSortType] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [colleges, setColleges] = useState([]);
  const [searchCollege, setSearchCollege] = useState("");
  const [rerender, setRerender] = useState(true);
  const [all_students, SetAll_students] = useState(0);
  const [all_students_onboarding_pending, SetAll_students_onboarding_pending] =
    useState(0);
  const [all_students_onboarding_invited, SetAll_students_onboarding_invited] =
    useState(0);
  const [
    all_students_onboarding_onboarded,
    SetAll_students_onboarding_onboarded,
  ] = useState(0);
  const [all_students_email_sent, SetAll_students_email_sent] = useState(0);
  const [all_students_email_not_sent, SetAll_students_email_not_sent] =
    useState(0);
    
  const [clusterSheetStudentsCount, setClusterSheetStudentsCount] = useState(0);

  // YUVA EC Assignment Modal state
  const [showEcModal, setShowEcModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const downloadExcelRef = useRef(null);

  // New state for filters
  const [filters, setFilters] = useState({
    region: "",
    state: "",
    onboardingStatus: "",
    emailStatus: "",
    collegeName: "",
  });

  // Upload and Download related states - UPDATED
  const [selectedOption, setSelectedOption] = useState(null);
  const [defaultOptions, setDefaultOptions] = useState([]);
  const [excelData, setExcelData] = useState(null);
  const [showUploadButton, setShowUploadButton] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);

  const isReadOnly = hasReadOnlyAccess();
  const isRegionEmUser = sessionStorage.getItem("userType") === "regionem";
  const navigate = useNavigate();
  const currentUserType = sessionStorage.getItem("userType");
  const userRegion = sessionStorage.getItem("region");
  // Get unique values for dropdowns
  const uniqueRegions = [
    ...new Set(allUsers.map((item) => item.region).filter(Boolean)),
  ];
  const uniqueStates = [
    ...new Set(allUsers.map((item) => item.state).filter(Boolean)),
  ];
  const uniqueColleges = [
    ...new Set(allUsers.map((item) => item.collegeName).filter(Boolean)),
  ];

  // Debounce search input
  useEffect(() => {
    const handle = setTimeout(
      () => setDebouncedSearch(searchQuery.trim()),
      350
    );
    return () => clearTimeout(handle);
  }, [searchQuery]);

  // Aggregate students uploaded via Thalir Cluster MOU sheets (admin view)
  useEffect(() => {
    const fetchClusterSheetStudents = async () => {
      if (activeTab !== "thalir") {
        setClusterSheetStudentsCount(0);
        return;
      }

      try {
        const adminId = process.env.REACT_APP_ADMIN_ID;
        const adminPassword = process.env.REACT_APP_ADMIN_PASSWORD;
        const basicAuth =
          adminId && adminPassword ? btoa(`${adminId}:${adminPassword}`) : null;

        const response = await fetch(
          `${SERVER_ORIGIN}/api/admin/auth/thalir-uploaded-sheets`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              ...(basicAuth ? { Authorization: `Basic ${basicAuth}` } : {}),
              "auth-token": sessionStorage.getItem("token"),
            },
          }
        );

        if (!response.ok) {
          setClusterSheetStudentsCount(0);
          return;
        }

        const result = await response.json();
        if (!result?.data || !Array.isArray(result.data)) {
          setClusterSheetStudentsCount(0);
          return;
        }

        const seen = new Set();
        let total = 0;
        for (const entry of result.data) {
          if (!entry?.instituteId) continue;
          if (seen.has(entry.instituteId)) continue;
          seen.add(entry.instituteId);
          total += entry.totalStudentsFromSheet || 0;
        }
        setClusterSheetStudentsCount(total);
      } catch (err) {
        console.error("Error fetching Thalir cluster student counts:", err);
        setClusterSheetStudentsCount(0);
      }
    };

    fetchClusterSheetStudents();
  }, [activeTab]);

  // Handle filter changes
  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
  };

  // Clear all filters
  const clearAllFilters = () => {
    setFilters({
      region: "",
      state: "",
      onboardingStatus: "",
      emailStatus: "",
      collegeName: "",
    });
    setSearchQuery("");
  };

  // Apply filters to data
  const filteredUsers = allUsers.filter((user) => {
    return (
      (!filters.region || user.region === filters.region) &&
      (!filters.state || user.state === filters.state) &&
      (!filters.onboardingStatus ||
        user.onboardingSatus === filters.onboardingStatus) &&
      (!filters.emailStatus || user.emailSentStatus === filters.emailStatus) &&
      (!filters.collegeName || user.collegeName === filters.collegeName)
    );
  });

  // UPLOAD AND DOWNLOAD FUNCTIONALITY - UPDATED WITH INSTITUTE FLOW
  const loadOptions = async (inputValue, callback) => {
    try {
      const response = await axios.get(
        `${SERVER_ORIGIN}/api/institute/auth/institute/all`,
        {
          params: {
            search: inputValue,
            page: 1,
            limit: 10,
            sortBy: "name",
            sortType: "asc",
            isThalir: activeTab === 'thalir' ? 'true' : 'false'
          },
        }
      );
      const options = response.data.filteredInstitutes.map((institute) => ({
        label: institute.name,
        value: institute._id,
        state: institute.state,
        city: institute.city,
      }));
      callback(options);
    } catch (error) {
      console.error("Error fetching institutes", error);
    }
  };

  useEffect(() => {
    const fetchDefaultOptions = async () => {
      try {
        const response = await axios.get(
          `${SERVER_ORIGIN}/api/institute/auth/institute/all`,
          {
            params: {
              search: "",
              page: 1,
              sortBy: "name",
              sortType: "asc",
              isThalir: activeTab === 'thalir' ? 'true' : 'false'
            },
          }
        );
        const options = response.data.filteredInstitutes.map((institute) => ({
          label: institute.name,
          value: institute._id,
          state: institute.state,
          city: institute.city,
        }));
        setDefaultOptions(options);
      } catch (error) {
        console.error("Error fetching default options", error);
      }
    };

    fetchDefaultOptions();
  }, [activeTab]);

  // Handle file selection and processing - ADDED FROM INSTITUTE
  const handleFileSelect = (data) => {
    setExcelData(data);
    setShowUploadButton(true);
    toast.success("Excel data retrieved successfully. Press the 'Upload' button to upload the file.");
  };

  // Handle file upload - ADDED FROM INSTITUTE
  const handleUpload = async () => {
    if (!excelData || !selectedOption) {
      toast.error("Please select an institute and ensure file data is loaded.");
      return;
    }

    try {
      setUploadLoading(true);
      const adminId = process.env.REACT_APP_ADMIN_ID;
      const adminPassword = process.env.REACT_APP_ADMIN_PASSWORD;
      const basicAuth = btoa(`${adminId}:${adminPassword}`);

      const response = await fetch(`${SERVER_ORIGIN}/api/admin/auth/upload-students`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Basic ${basicAuth}`,
          'auth-token': sessionStorage.getItem('token'),
        },
        body: JSON.stringify({
          excelData: excelData,
          instituteId: selectedOption.value,
          instituteName: selectedOption.label,
          uploadedBy: sessionStorage.getItem('_id'),
          isThalir: activeTab === 'thalir'
        }),
      });

      const result = await response.json();
      setUploadLoading(false);

      if (response.ok) {
        toast.success('Students uploaded successfully!');
        setShowUploadButton(false);
        setExcelData(null);
        // Refresh the user list
        setRerender(!rerender);
      } else {
        toast.error(result.message || 'Upload failed');
      }
    } catch (error) {
      setUploadLoading(false);
      toast.error('Upload failed. Please try again.');
    }
  };

  const renderOnboardingStatus = (params) => {
    let statusColor;
    let statusText;
    let textColor;

    switch (params.value) {
      case "invited":
        statusColor = yellow[500];
        statusText = "Invited";
        textColor = "black";
        break;
      case "pending":
        statusColor = red[500];
        statusText = "Pending";
        textColor = "white";
        break;
      case "onboarded":
        statusColor = green[500];
        statusText = "Onboarded";
        textColor = "white";
        break;
      default:
        statusColor = "transparent";
        statusText = "Unknown";
        textColor = "white";
    }
    return (
      <Badge>
        <span
          style={{
            backgroundColor: statusColor,
            padding: "6.4px 16px",
            borderRadius: "8px",
            color: textColor,
          }}
        >
          {statusText}
        </span>
      </Badge>
    );
  };

  const renderEmailStatus = (params) => {
    let statusColor;
    let statusText;
    let textColor;

    switch (params.value) {
      case "sent":
        statusColor = green[500];
        statusText = "Sent";
        textColor = "white";
        break;
      case "not sent":
        statusColor = red[500];
        statusText = "Not Sent";
        textColor = "white";
        break;
      default:
        statusColor = "transparent";
        statusText = "Unknown";
        textColor = "white";
    }
    return (
      <Badge>
        <span
          style={{
            borderRadius: "8px",
            backgroundColor: statusColor,
            padding: "6.4px 16px",
            color: textColor,
          }}
        >
          {statusText}
        </span>
      </Badge>
    );
  };

  const renderYuvaECStatus = (params) => {
    const isEC = params.row.isYuvaEC || false;
    const ecPosition = params.row.yuvaEcPosition || "";
    const effectiveFrom = params.row.yuvaEcEffectiveFrom;
    const effectiveTo = params.row.yuvaEcEffectiveTo;

    const now = new Date();
    const fromDate = effectiveFrom ? new Date(effectiveFrom) : null;
    const toDate = effectiveTo ? new Date(effectiveTo) : null;
    const isActive = fromDate && toDate && now >= fromDate && now <= toDate;

    if (isEC && isActive) {
      return (
        <Badge>
          <span
            style={{
              backgroundColor: green[500],
              padding: "6.4px 16px",
              borderRadius: "8px",
              color: "white",
              fontSize: "12px",
            }}
          >
            {ecPosition || "YUVA EC"}
          </span>
        </Badge>
      );
    } else if (isEC && !isActive) {
      return (
        <Badge>
          <span
            style={{
              backgroundColor: yellow[600],
              padding: "6.4px 16px",
              borderRadius: "8px",
              color: "white",
              fontSize: "12px",
            }}
          >
            Expired
          </span>
        </Badge>
      );
    } else {
      return (
        <Badge>
          <span
            style={{
              backgroundColor: "#f5f5f5",
              padding: "6.4px 16px",
              borderRadius: "8px",
              color: "#666",
              fontSize: "12px",
            }}
          >
            Not Assigned
          </span>
        </Badge>
      );
    }
  };

  // Base columns that are always shown
  const baseColumns = [
    {
      field: "id",
      headerName: "ID",
      align: "center",
      width: 80,
    },
    {
      field: "yuvaStudentId",
      headerName: "Student ID",
      align: "center",
      width: 160,
      valueGetter: (params) => params.row.yuvaStudentId || "-",
    },
    {
      field: "fName",
      align: "center",
      headerName: "Full Name",
      width: 180,
      valueGetter: (params) =>
        `${params.row.fName || ""} ${params.row.lName || ""}`.trim(),
    },
    ...(!isRegionEmUser
      ? [
        {
          field: "email",
          headerName: "Email",
          align: "center",
          width: 220,
        },
      ]
      : []),
    {
      field: "collegeName",
      headerName: "College Name",
      align: "center",
      width: 200,
    },
    {
      field: "passOutYear",
      headerName: "Passout Year",
      align: "center",
      width: 130,
    },
    {
      field: "chapterName",
      headerName: "Chapter Name",
      align: "center",
      width: 150,
    },
    {
      field: "region",
      headerName: "Region",
      align: "center",
      width: 120,
    },
    {
      field: "onboardingSatus",
      headerName: "Onboarding Status",
      width: 150,
      align: "center",
      renderCell: renderOnboardingStatus,
    },
    {
      field: "gender",
      headerName: "Gender",
      align: "center",
      width: 100,
    },
    // {
    //   field: "branch",
    //   headerName: "Branch",
    //   align: "center",
    //   width: 150,
    // },
    {
      field: "updatedAt",
      headerName: "Last Updated",
      align: "center",
      width: 140,
      valueGetter: (params) => params.row.updatedAt || params.row.createdAt || null,
      valueFormatter: (params) => {
        if (!params.value) return "-";
        const d = new Date(params.value);
        return d.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      },
      renderCell: (params) => (
        <span style={{ whiteSpace: "normal", lineHeight: 1.3 }}>
          {params.formattedValue || params.value || "-"}
        </span>
      ),
    },
    {
      field: "registrationType",
      headerName: "Registration Type",
      align: "center",
      width: 150,
      renderCell: (params) => {
        const isPublic = params.row.isPublicRegistration || false;
        return (
          <Badge>
            <span
              style={{
                backgroundColor: isPublic ? "#007bff" : "#6c757d",
                padding: "4px 8px",
                borderRadius: "6px",
                color: "white",
                fontSize: "11px",
                fontWeight: "500",
              }}
            >
              {isPublic ? "Public Link" : "Regular"}
            </span>
          </Badge>
        );
      },
    },
    {
      field: "viewDetails",
      headerName: "View Details",
      type: "actions",
      width: 140,
      align: "center",
      renderCell: (params) => (
        <button
          className={css.viewBtn}
          onClick={() => navigate(`/admin/users/${params.row.userId}`)}
        >
          View Details
        </button>
      ),
    },
    {
      field: "editDetails",
      headerName: "Edit",
      type: "actions",
      width: 120,
      align: "center",
      // renderCell: (params) => (
      //   <button
      //     className={css.viewBtn}
      //     onClick={() =>
      //       navigate(
      //         `/user/register/no_id?edit=true&id=${encodeURIComponent(
      //           params.row.userId
      //         )}&tab=${activeTab}`
      //       )
      //     }
      //   >
      //     Edit
      //   </button>
      // ),
      renderCell: (params) =>
        isReadOnly ? (
          <button className={`${css.viewBtn} ${css.disabledBtn}`} disabled>
            Edit
          </button>
        ) : (
          <button
            className={css.viewBtn}
            onClick={() =>
              navigate(
                `/user/register/no_id?edit=true&id=${encodeURIComponent(
                  params.row.userId
                )}&tab=${activeTab}`
              )
            }
          >
            Edit
          </button>
        ),
    },
  ];

  // EC-specific columns (shown when isEC is true)
  const ecColumns = [
    {
      field: "yuvaEcEffectiveFrom",
      headerName: "Effective From",
      align: "center",
      width: 140,
      valueFormatter: (params) => {
        if (!params.value) return "";
        const date = new Date(params.value);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      },
    },
    {
      field: "yuvaEcEffectiveTo",
      headerName: "Effective To",
      align: "center",
      width: 140,
      valueFormatter: (params) => {
        if (!params.value) return "";
        const date = new Date(params.value);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      },
    },

  ];

  // Institute-specific columns (shown when activeTab is "institute")
  const instituteColumns = [
    {
      field: 'lastLoginDate', headerName: 'Last Login', align: 'center', width: 170, valueGetter: (params) => {
        if (!params.value) return "Never";
        const date = new Date(params.value);
        return date.toLocaleString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      }
    },
    {
      field: "yuvaEcStatus",
      headerName: "YUVA EC Status",
      align: "center",
      width: 150,
      renderCell: renderYuvaECStatus,
    },
    {
      field: "assignEC",
      headerName: "Assign YUVA EC",
      type: "actions",
      width: 160,
      align: "center",
      // renderCell: (params) => (
      //   <button
      //     className={css.editBtn}
      //     style={{
      //       backgroundColor: "#28a745",
      //       border: "none",
      //       color: "white",
      //       padding: "6px 12px",
      //       borderRadius: "4px",
      //       fontSize: "12px",
      //       cursor: "pointer",
      //     }}
      //     onClick={() => {
      //       setSelectedStudent(params.row);
      //       setShowEcModal(true);
      //     }}
      //   >
      //     Assign YUVA EC
      //   </button>
      // ),
      renderCell: (params) =>
        isReadOnly ? (
          <button className={`${css.viewBtn} ${css.disabledBtn}`} disabled style={{
            backgroundColor: "#28a745",
            border: "none",
            color: "white",
            padding: "6px 12px",
            borderRadius: "4px",
            fontSize: "12px",
            cursor: "pointer",
          }}>
            Read Only
          </button>
        ) : (
          <button
            className={css.editBtn}
            style={{
              backgroundColor: "#28a745",
              border: "none",
              color: "white",
              padding: "6px 12px",
              borderRadius: "4px",
              fontSize: "12px",
              cursor: "pointer",
            }}
            onClick={() => {
              setSelectedStudent(params.row);
              setShowEcModal(true);
            }}
          >
            Assign YUVA EC
          </button>
        ),
    },
  ];

  // Build columns array based on conditions
  let columns = [...baseColumns];

  // Add EC effective date columns when isEC is true
  if (isECFromUrl === "true") {
    columns = [...columns, ...ecColumns];
  }

  // Add institute-specific columns when activeTab is "institute"
  if (activeTab === "institute") {
    columns = [...columns, ...instituteColumns];
  }

  const rows = filteredUsers.map((user, idx) => ({ ...user, id: idx + 1 }));

  // Subtle elevation and lift-on-hover for header cards
  const ElevateOnHover = ({ children }) => {
    const [hovered, setHovered] = useState(false);
    return (
      <div
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          borderRadius: 12,
          background: "#ffffff",
          transition: "box-shadow 200ms ease, transform 200ms ease",
          boxShadow: hovered
            ? "0 8px 24px rgba(16,24,40,.12)"
            : "0 1px 3px rgba(16,24,40,.06)",
          transform: hovered ? "translateY(-2px)" : "none",
        }}
      >
        {children}
      </div>
    );
  };

  // Reusable stat card with icon and soft gradient
  const StatCard = ({ title, value, icon, gradient }) => (
    <ElevateOnHover>
      <div className={css.dashboardCard} style={{ overflow: "hidden" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "12px 16px",
          }}
        >
          <h3
            className={css.cardTitle}
            style={{ margin: 0, fontSize: "14px", fontWeight: "600" }}
          >
            {title}
          </h3>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: gradient,
              color: "#fff",
            }}
          >
            <FontAwesomeIcon icon={icon} />
          </div>
        </div>
        <div className={css.cardContent} style={{ paddingTop: 0 }}>
          <div className={css.mainNumber}>{value}</div>
        </div>
      </div>
    </ElevateOnHover>
  );

  function handleUsersClick() {
    navigate(`/user/register/no_id?tab=${activeTab || "institute"}`);
  }

  // Your existing useEffect hooks remain the same
  useEffect(() => {
    async function getCollegeName() {
      setIsLoading(true);
      try {
        const adminId = process.env.REACT_APP_ADMIN_ID;
        const adminPassword = process.env.REACT_APP_ADMIN_PASSWORD;
        const basicAuth = btoa(`${adminId}:${adminPassword}`);

        const response = await fetch(
          `${SERVER_ORIGIN}/api/admin/auth/users/college-names`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Basic ${basicAuth}`,
              "auth-token": sessionStorage.getItem("token"),
            },
          }
        );

        const result = await response.json();
        setIsLoading(false);

        if (response.status >= 400 && response.status < 600) {
          if (response.status === 401) {
            navigate("/admin/login");
          } else if (response.status === 500) {
            toast.error(result.statusText);
          }
        }
      } catch (err) {
        setIsLoading(false);
      }
    }

    getCollegeName();
  }, [navigate]);

  useEffect(() => {
    setActiveTab(tabFromUrl);
    async function getAllUsers() {
      setIsLoading(true);
      try {
        const adminId = process.env.REACT_APP_ADMIN_ID;
        const adminPassword = process.env.REACT_APP_ADMIN_PASSWORD;
        const basicAuth = btoa(`${adminId}:${adminPassword}`);
        let regionQuery = "";
        if (currentUserType === "regionem" && userRegion) {
          regionQuery = `&region=${userRegion}`;
        }
        const response = await fetch(
          `${SERVER_ORIGIN}/api/admin/auth/users/all?page=${page}&limit=${limit}&search=${debouncedSearch}&sortBy=fName&sortType=${sortType === true ? "asc" : "desc"
          }&collegeName=${searchCollege}&isThalir=${activeTab === "thalir" ? "true" : "false"
          }&isEC=${isECFromUrl || "false"}${regionQuery}`,
          {
            headers: {
              method: "GET",
              "Content-Type": "application/json",
              Authorization: `Basic ${basicAuth}`,
              "auth-token": sessionStorage.getItem("token"),
            },
          }
        );

        const result = await response.json();
        setIsLoading(false);

        if (response.status >= 400 && response.status < 600) {
          if (response.status === 401) {
            navigate("/admin/login");
          } else if (response.status === 500) {
            toast.error(result.statusText);
          }
        } else if (response.ok && response.status === 200) {
          setAllUsers(result.filteredUsers);
          setTotalPages(result.totalPages);
        }
      } catch (err) {
        setIsLoading(false);
      }
    }

    async function counting() {
      setIsLoading(true);
      try {
        let regionQuery = "";
        if (currentUserType === "regionem" && userRegion) {
          regionQuery = `&region=${userRegion}`;
        }
        const response = await fetch(
          `${SERVER_ORIGIN}/api/admin/auth/student-counting?isThalir=${activeTab === "thalir" ? "true" : "false"
          }&isEC=${isECFromUrl || "false"}${regionQuery}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "auth-token": sessionStorage.getItem("token"),
            },
          }
        );

        const result = await response.json();
        if (response.status >= 400 && response.status < 600) {
          if (response.status === 401) {
            navigate("/login?userType=institute");
          }
        } else if (response.ok && response.status === 200) {
          SetAll_students(result.data.all_students);
          SetAll_students_onboarding_pending(
            result.data.all_students_onboarding_pending
          );
          SetAll_students_onboarding_invited(
            result.data.all_students_onboarding_invited
          );
          SetAll_students_onboarding_onboarded(
            result.data.all_students_onboarding_onboarded
          );
          SetAll_students_email_sent(result.data.all_students_email_sent);
          SetAll_students_email_not_sent(
            result.data.all_students_email_not_sent
          );
            setClusterSheetStudentsCount(
            result.data.totalUploadedStudents
          );
        } else {
          navigate("/login?userType=institute");
        }
      } catch (err) {
        // Handle error
      }
      setIsLoading(false);
    }

    counting();
    getAllUsers();
  }, [
    page,
    sortType,
    debouncedSearch,
    searchCollege,
    navigate,
    limit,
    activeTab,
    rerender, // Added rerender to refresh data after upload
  ]);

  function CustomColumnMenu(props) {
    return (
      <GridColumnMenu
        {...props}
        slotProps={{
          columnMenuUserItem: {
            displayOrder: 15,
            myCustomValue: "Do custom action",
            myCustomHandler: () => alert("Custom handler fired"),
          },
        }}
      />
    );
  }

  return (
    <div className={css.outerDiv}>
      {/* Header Section */}
      <div className={css.headerSection}>
        <div className={css.headerLeft}>
          <BackButton
            text="Back to Dashboard"
            customPath={getDashboardPath()}
          />
        </div>
      </div>

      {isReadOnly && (
        <div className={css.readOnlyAlert}>
          <div className="alert alert-info" role="alert">
            <strong>Read-Only Access:</strong> You can view all student data but
            cannot add, edit, or upload new students.
          </div>
        </div>
      )}

      <div className="my-4">
        <SecCard>
          <Row className="g-4">
            <div
              className={`${css.serviceHeader} d-flex justify-content-between align-items-center mb-3`}
            >
              <div className={css.serviceName}>Students Data</div>

              <div className={css.headerRightSection}>
                {!isReadOnly && (
                  <div className={css.actionButtons}>
                    <button className={css.addBtn} onClick={handleUsersClick}>
                      <FontAwesomeIcon icon={faPlus} className={css.addIcon} />
                      Add Student
                    </button>

                    {/* <ActionsDropdown
                      onDownload={() =>
                        document
                          .querySelector(`.${css.downloadExcelBtn}`)
                          ?.click()
                      }
                    /> */}

                    {/* Hidden triggers for download */}
                    <div style={{ display: "none" }}>
                      <DownloadExcel
                        ref={downloadExcelRef}
                        className={css.downloadExcelBtn}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* UPLOAD AND DOWNLOAD CONTROLS - UPDATED WITH INSTITUTE FLOW */}
            <div className={css.uploadDownloadSection}>
              <div className="row align-items-center">
                <div className="col-md-4">
                  <div style={{ marginBottom: "1rem" }}>
                    <AsyncSelect
                      cacheOptions
                      loadOptions={loadOptions}
                      defaultOptions={defaultOptions}
                      onChange={(selectedOption) => {
                        setSelectedOption(selectedOption);
                      }}
                      value={selectedOption}
                      placeholder="Select / Search Institute"
                    />
                  </div>
                </div>
                <div className="col-md-8">
                  <div className="d-flex justify-content-end gap-3 align-items-center">
                    <DownloadExcel />

                    {/* Custom File Upload with Upload Button - UPDATED */}
                    <div className="d-flex gap-2 align-items-center">
                      <FileUpload
                        onFileProcessed={handleFileSelect}
                        collegeName={selectedOption}
                      />

                      {showUploadButton && (
                        <button
                          className={`${css.uploadBtn} commonBtn`}
                          onClick={handleUpload}
                          disabled={uploadLoading || !selectedOption}
                        >
                          {uploadLoading ? (
                            <span>Uploading...</span>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faUpload} style={{ color: "#ffffff", marginRight: "5px" }} />
                              Upload Data
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <Col md={6} lg={6} sm={12} xs={12}>
              <StatCard
                title={
                  activeTab === "institute"
                    ? "YUVA Students"
                    : "Thalir Students"
                }
                value={all_students}
                icon={faUserGraduate}
                gradient="linear-gradient(135deg,#6366f1,#8b5cf6)"
              />
            </Col>

            {activeTab === "thalir" && (
              <Col md={6} lg={6} sm={12} xs={12}>
                <StatCard
                  title="From Uploaded Sheet Data"
                  value={clusterSheetStudentsCount}
                  icon={faUpload}
                  gradient="linear-gradient(135deg,#059669,#10b981)"
                />
              </Col>
            )}

            <Col md={6} lg={6} sm={12} xs={12}>
              <StatCard
                title="Onboarding Status"
                value={
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: "12px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        flex: 1,
                      }}
                    >
                      <span
                        style={{
                          fontSize: "12px",
                          color: "#6b7280",
                          fontWeight: "500",
                          marginBottom: "4px",
                        }}
                      >
                        Total
                      </span>
                      <span
                        style={{
                          fontSize: "18px",
                          color: "#1f2937",
                          fontWeight: "600",
                        }}
                      >
                        {all_students_onboarding_onboarded +
                          all_students_onboarding_invited +
                          all_students_onboarding_pending}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        flex: 1,
                      }}
                    >
                      <span
                        style={{
                          fontSize: "12px",
                          color: "#6b7280",
                          fontWeight: "500",
                          marginBottom: "4px",
                        }}
                      >
                        Onboarded
                      </span>
                      <span
                        style={{
                          fontSize: "18px",
                          color: "#1f2937",
                          fontWeight: "600",
                        }}
                      >
                        {all_students_onboarding_onboarded}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        flex: 1,
                      }}
                    >
                      <span
                        style={{
                          fontSize: "12px",
                          color: "#6b7280",
                          fontWeight: "500",
                          marginBottom: "4px",
                        }}
                      >
                        Invited
                      </span>
                      <span
                        style={{
                          fontSize: "18px",
                          color: "#1f2937",
                          fontWeight: "600",
                        }}
                      >
                        {all_students_onboarding_invited}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        flex: 1,
                      }}
                    >
                      <span
                        style={{
                          fontSize: "12px",
                          color: "#6b7280",
                          fontWeight: "500",
                          marginBottom: "4px",
                        }}
                      >
                        Pending
                      </span>
                      <span
                        style={{
                          fontSize: "18px",
                          color: "#1f2937",
                          fontWeight: "600",
                        }}
                      >
                        {all_students_onboarding_pending}
                      </span>
                    </div>
                  </div>
                }
                icon={faUserCheck}
                gradient="linear-gradient(135deg,#f59e0b,#f97316)"
              />
            </Col>
          </Row>
        </SecCard>
      </div>

      {/* Search and Filters Section */}
      <div className={css.searchFiltersSection}>
        <div className={css.filtersContainer}>
          <div className={css.filterGroup}>
            <label className={css.filterLabel}>Search</label>
            <div className={css.searchInputWrapper}>
              <input
                type="text"
                value={searchQuery}
                placeholder="Search from the list"
                onChange={(e) => setSearchQuery(e.target.value)}
                className={css.searchInput}
              />
            </div>
          </div>

          <div className={css.filterGroup}>
            <label className={css.filterLabel}>Region</label>
            <select
              value={filters.region}
              onChange={(e) => handleFilterChange("region", e.target.value)}
              className={css.filterSelect}
            >
              <option value="">All Regions</option>
              {uniqueRegions.map((region) => (
                <option key={region} value={region}>
                  {region}
                </option>
              ))}
            </select>
          </div>

          <div className={css.filterGroup}>
            <label className={css.filterLabel}>College</label>
            <select
              value={filters.collegeName}
              onChange={(e) =>
                handleFilterChange("collegeName", e.target.value)
              }
              className={css.filterSelect}
            >
              <option value="">All Colleges</option>
              {uniqueColleges.map((college) => (
                <option key={college} value={college}>
                  {college}
                </option>
              ))}
            </select>
          </div>

          <button onClick={clearAllFilters} className={css.clearFiltersBtn}>
            Clear All
          </button>
        </div>
      </div>

      {/* Data Grid Section */}
      {isLoading ? (
        <Loader />
      ) : (
        <div className={css.dataGridContainer}>
          <DataGrid
            rows={rows}
            columns={columns}
            autoHeight
            density="compact"
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10, page: 0 },
              },
            }}
            pageSizeOptions={[10, 25, 50]}
            disableRowSelectionOnClick
            sx={{
              borderRadius: 2,
              border: "1px solid #e5e7eb",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f9fafb",
                color: "#111827",
                fontWeight: 600,
              },
              "& .MuiDataGrid-row:nth-of-type(odd)": {
                backgroundColor: "#fcfcfd",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "#eef2ff",
              },
            }}
            slots={{ columnMenu: CustomColumnMenu, toolbar: GridToolbar }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 300 },
              },
            }}
            loading={isLoading}
          />
        </div>
      )}

      <YuvaEcAssignmentModal
        show={showEcModal}
        onHide={() => setShowEcModal(false)}
        student={selectedStudent}
        onSuccess={() => {
          setRerender(!rerender);
        }}
      />
    </div>
  );
};

const ActionsDropdown = ({ onDownload }) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleDropdown = () => setIsOpen(!isOpen);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(`.${css.dropdownContainer}`)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={css.dropdownContainer}>
      <button
        className={css.ellipsisBtn}
        onClick={toggleDropdown}
        aria-label="More actions"
      >
        <FontAwesomeIcon icon={faEllipsisVertical} />
      </button>

      {isOpen && (
        <div className={css.dropdownMenu}>
          <button
            className={css.dropdownItem}
            onClick={() => {
              onDownload?.();
              setIsOpen(false);
            }}
          >
            <FontAwesomeIcon icon={faDownload} className={css.dropdownIcon} />
            Download Excel
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;

// import React, { useEffect, useState, useRef } from "react";
// import { useNavigate, useSearchParams } from "react-router-dom";
// import axios from "axios";
// import { toast } from "react-hot-toast";
// import FileUpload from "../../components/common/FileUpload";
// import { DataGrid, GridColumnMenu, GridToolbar } from "@mui/x-data-grid";
// import Loader from "../../components/common/Loader";
// import Badge from "@mui/material/Badge";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import {
//   faPlus,
//   faMagnifyingGlass,
//   faUserGraduate,
//   faEnvelope,
//   faUserCheck,
//   faIdBadge,
//   faEllipsisVertical,
//   faDownload,
//   faUpload,
// } from "@fortawesome/free-solid-svg-icons";
// import SecCard from "../../components/common/SecCard";
// import { Row, Col } from "react-bootstrap";
// import { green, yellow, red } from "@mui/material/colors";
// import { SERVER_ORIGIN } from "../../utilities/constants";
// import {
//   hasReadOnlyAccess,
//   getDashboardPath,
// } from "../../utilities/helper_functions";
// import HeaderCard from "../../components/common/HeaderCard";
// import BackButton from "../../components/common/BackButton";
// import YuvaEcAssignmentModal from "../../components/common/YuvaEcAssignmentModal";
// import css from "../../css/admin/users-page.module.css";
// import DownloadExcel from "../../components/common/DownloadExcel";
// import AsyncSelect from "react-select/async";

// const AdminUsers = () => {
//   const [searchParams] = useSearchParams();
//   const tabFromUrl = searchParams.get("tab");
//   const isECFromUrl = searchParams.get("isEC");

//   const [activeTab, setActiveTab] = useState(tabFromUrl || "institute");
//   const [isLoading, setIsLoading] = useState(false);
//   const [limit, setLimit] = useState(0);
//   const [allUsers, setAllUsers] = useState([]);
//   const [page, setPage] = useState(1);
//   const [sortType, setSortType] = useState(true);
//   const [searchQuery, setSearchQuery] = useState("");
//   const [debouncedSearch, setDebouncedSearch] = useState("");
//   const [totalPages, setTotalPages] = useState(0);
//   const [colleges, setColleges] = useState([]);
//   const [searchCollege, setSearchCollege] = useState("");
//   const [rerender, setRerender] = useState(true);
//   const [all_students, SetAll_students] = useState(0);
//   const [all_students_onboarding_pending, SetAll_students_onboarding_pending] =
//     useState(0);
//   const [all_students_onboarding_invited, SetAll_students_onboarding_invited] =
//     useState(0);
//   const [
//     all_students_onboarding_onboarded,
//     SetAll_students_onboarding_onboarded,
//   ] = useState(0);
//   const [all_students_email_sent, SetAll_students_email_sent] = useState(0);
//   const [all_students_email_not_sent, SetAll_students_email_not_sent] =
//     useState(0);

//   // YUVA EC Assignment Modal state
//   const [showEcModal, setShowEcModal] = useState(false);
//   const [selectedStudent, setSelectedStudent] = useState(null);
//   const downloadExcelRef = useRef(null);
//   const fileUploadRef = useRef(null);

//   // New state for filters
//   const [filters, setFilters] = useState({
//     region: "",
//     state: "",
//     onboardingStatus: "",
//     emailStatus: "",
//     collegeName: "",
//   });

//   // Upload and Download related states
//   const [selectedOption, setSelectedOption] = useState(null);
//   const [defaultOptions, setDefaultOptions] = useState([]);
//   const [regisForm, setRegisForm] = useState({
//     userId: "",
//     email: "",
//     fName: "",
//     mName: "",
//     lName: "",
//     collegeName: "",
//     region: "",
//     branch: "",
//     phone: "",
//     addLine1: "",
//     addLine2: "",
//     city: "",
//     pincode: "",
//     country: "India",
//     state: "",
//     passOutYear: "",
//     createdBy:
//       sessionStorage.getItem("token") !== null
//         ? sessionStorage.getItem("_id")
//         : "",
//   });

//   const isReadOnly = hasReadOnlyAccess();
//   const navigate = useNavigate();

//   // Get unique values for dropdowns
//   const uniqueRegions = [
//     ...new Set(allUsers.map((item) => item.region).filter(Boolean)),
//   ];
//   const uniqueStates = [
//     ...new Set(allUsers.map((item) => item.state).filter(Boolean)),
//   ];
//   const uniqueColleges = [
//     ...new Set(allUsers.map((item) => item.collegeName).filter(Boolean)),
//   ];

//   // Debounce search input
//   useEffect(() => {
//     const handle = setTimeout(
//       () => setDebouncedSearch(searchQuery.trim()),
//       350
//     );
//     return () => clearTimeout(handle);
//   }, [searchQuery]);

//   // Handle filter changes
//   const handleFilterChange = (filterName, value) => {
//     setFilters((prev) => ({
//       ...prev,
//       [filterName]: value,
//     }));
//   };

//   // Clear all filters
//   const clearAllFilters = () => {
//     setFilters({
//       region: "",
//       state: "",
//       onboardingStatus: "",
//       emailStatus: "",
//       collegeName: "",
//     });
//     setSearchQuery("");
//   };

//   // Apply filters to data
//   const filteredUsers = allUsers.filter((user) => {
//     return (
//       (!filters.region || user.region === filters.region) &&
//       (!filters.state || user.state === filters.state) &&
//       (!filters.onboardingStatus ||
//         user.onboardingSatus === filters.onboardingStatus) &&
//       (!filters.emailStatus || user.emailSentStatus === filters.emailStatus) &&
//       (!filters.collegeName || user.collegeName === filters.collegeName)
//     );
//   });

//   // UPLOAD AND DOWNLOAD FUNCTIONALITY - ADDED FROM INSTITUTE
//   const loadOptions = async (inputValue, callback) => {
//     try {
//       const response = await axios.get(
//         `${SERVER_ORIGIN}/api/institute/auth/institute/all`,
//         {
//           params: {
//             search: inputValue,
//             page: 1,
//             limit: 10,
//             sortBy: "name",
//             sortType: "asc",
//             isThalir: activeTab === 'thalir' ? 'true' : 'false'
//           },
//         }
//       );
//       const options = response.data.filteredInstitutes.map((institute) => ({
//         label: institute.name,
//         value: institute._id,
//         state: institute.state,
//         city: institute.city,
//       }));
//       callback(options);
//     } catch (error) {
//       console.error("Error fetching institutes", error);
//     }
//   };

//   useEffect(() => {
//     const fetchDefaultOptions = async () => {
//       try {
//         const response = await axios.get(
//           `${SERVER_ORIGIN}/api/institute/auth/institute/all`,
//           {
//             params: {
//               search: "",
//               page: 1,
//               sortBy: "name",
//               sortType: "asc",
//               isThalir: activeTab === 'thalir' ? 'true' : 'false'
//             },
//           }
//         );
//         const options = response.data.filteredInstitutes.map((institute) => ({
//           label: institute.name,
//           value: institute._id,
//           state: institute.state,
//           city: institute.city,
//         }));
//         setDefaultOptions(options);
//       } catch (error) {
//         console.error("Error fetching default options", error);
//       }
//     };

//     fetchDefaultOptions();
//   }, [activeTab]); // Add activeTab dependency

//   const renderOnboardingStatus = (params) => {
//     let statusColor;
//     let statusText;
//     let textColor;

//     switch (params.value) {
//       case "invited":
//         statusColor = yellow[500];
//         statusText = "Invited";
//         textColor = "black";
//         break;
//       case "pending":
//         statusColor = red[500];
//         statusText = "Pending";
//         textColor = "white";
//         break;
//       case "onboarded":
//         statusColor = green[500];
//         statusText = "Onboarded";
//         textColor = "white";
//         break;
//       default:
//         statusColor = "transparent";
//         statusText = "Unknown";
//         textColor = "white";
//     }
//     return (
//       <Badge>
//         <span
//           style={{
//             backgroundColor: statusColor,
//             padding: "6.4px 16px",
//             borderRadius: "8px",
//             color: textColor,
//           }}
//         >
//           {statusText}
//         </span>
//       </Badge>
//     );
//   };

//   const renderEmailStatus = (params) => {
//     let statusColor;
//     let statusText;
//     let textColor;

//     switch (params.value) {
//       case "sent":
//         statusColor = green[500];
//         statusText = "Sent";
//         textColor = "white";
//         break;
//       case "not sent":
//         statusColor = red[500];
//         statusText = "Not Sent";
//         textColor = "white";
//         break;
//       default:
//         statusColor = "transparent";
//         statusText = "Unknown";
//         textColor = "white";
//     }
//     return (
//       <Badge>
//         <span
//           style={{
//             borderRadius: "8px",
//             backgroundColor: statusColor,
//             padding: "6.4px 16px",
//             color: textColor,
//           }}
//         >
//           {statusText}
//         </span>
//       </Badge>
//     );
//   };

//   const renderYuvaECStatus = (params) => {
//     const isEC = params.row.isYuvaEC || false;
//     const ecPosition = params.row.yuvaEcPosition || "";
//     const effectiveFrom = params.row.yuvaEcEffectiveFrom;
//     const effectiveTo = params.row.yuvaEcEffectiveTo;

//     const now = new Date();
//     const fromDate = effectiveFrom ? new Date(effectiveFrom) : null;
//     const toDate = effectiveTo ? new Date(effectiveTo) : null;
//     const isActive = fromDate && toDate && now >= fromDate && now <= toDate;

//     if (isEC && isActive) {
//       return (
//         <Badge>
//           <span
//             style={{
//               backgroundColor: green[500],
//               padding: "6.4px 16px",
//               borderRadius: "8px",
//               color: "white",
//               fontSize: "12px",
//             }}
//           >
//             {ecPosition || "YUVA EC"}
//           </span>
//         </Badge>
//       );
//     } else if (isEC && !isActive) {
//       return (
//         <Badge>
//           <span
//             style={{
//               backgroundColor: yellow[600],
//               padding: "6.4px 16px",
//               borderRadius: "8px",
//               color: "white",
//               fontSize: "12px",
//             }}
//           >
//             Expired
//           </span>
//         </Badge>
//       );
//     } else {
//       return (
//         <Badge>
//           <span
//             style={{
//               backgroundColor: "#f5f5f5",
//               padding: "6.4px 16px",
//               borderRadius: "8px",
//               color: "#666",
//               fontSize: "12px",
//             }}
//           >
//             Not Assigned
//           </span>
//         </Badge>
//       );
//     }
//   };

//   // Base columns that are always shown
//   const baseColumns = [
//     {
//       field: "id",
//       headerName: "ID",
//       align: "center",
//       width: 80,
//     },
//     {
//       field: "fName",
//       align: "center",
//       headerName: "Full Name",
//       width: 180,
//       valueGetter: (params) =>
//         `${params.row.fName || ""} ${params.row.lName || ""}`.trim(),
//     },
//     {
//       field: "email",
//       headerName: "Email",
//       align: "center",
//       width: 220,
//     },
//     {
//       field: "collegeName",
//       headerName: "College Name",
//       align: "center",
//       width: 200,
//     },
//     {
//       field: "passOutYear",
//       headerName: "Passout Year",
//       align: "center",
//       width: 130,
//     },
//     {
//       field: "chapterName",
//       headerName: "Chapter Name",
//       align: "center",
//       width: 150,
//     },
//     {
//       field: "region",
//       headerName: "Region",
//       align: "center",
//       width: 120,
//     },
//     {
//       field: "onboardingSatus",
//       headerName: "Onboarding Status",
//       width: 150,
//       align: "center",
//       renderCell: renderOnboardingStatus,
//     },
//     {
//       field: "gender",
//       headerName: "Gender",
//       align: "center",
//       width: 100,
//     },
//     {
//       field: "branch",
//       headerName: "Branch",
//       align: "center",
//       width: 150,
//     },
//     {
//       field: "registrationType",
//       headerName: "Registration Type",
//       align: "center",
//       width: 150,
//       renderCell: (params) => {
//         const isPublic = params.row.isPublicRegistration || false;
//         return (
//           <Badge>
//             <span
//               style={{
//                 backgroundColor: isPublic ? "#007bff" : "#6c757d",
//                 padding: "4px 8px",
//                 borderRadius: "6px",
//                 color: "white",
//                 fontSize: "11px",
//                 fontWeight: "500",
//               }}
//             >
//               {isPublic ? "Public Link" : "Regular"}
//             </span>
//           </Badge>
//         );
//       },
//     },
//     {
//       field: "viewDetails",
//       headerName: "View Details",
//       type: "actions",
//       width: 140,
//       align: "center",
//       renderCell: (params) => (
//         <button
//           className={css.viewBtn}
//           onClick={() => navigate(`/admin/users/${params.row.userId}`)}
//         >
//           View Details
//         </button>
//       ),
//     },
//     {
//       field: "editDetails",
//       headerName: "Edit",
//       type: "actions",
//       width: 120,
//       align: "center",
//       renderCell: (params) => (
//         <button
//           className={css.viewBtn}
//           onClick={() =>
//             navigate(
//               `/user/register/no_id?edit=true&id=${encodeURIComponent(
//                 params.row.userId
//               )}&tab=${activeTab}`
//             )
//           }
//         >
//           Edit
//         </button>
//       ),
//     },
//   ];

//   // EC-specific columns (shown when isEC is true)
//   const ecColumns = [
//     {
//       field: "yuvaEcEffectiveFrom",
//       headerName: "Effective From",
//       align: "center",
//       width: 140,
//       valueFormatter: (params) => {
//         if (!params.value) return "";
//         const date = new Date(params.value);
//         const day = String(date.getDate()).padStart(2, "0");
//         const month = String(date.getMonth() + 1).padStart(2, "0");
//         const year = date.getFullYear();
//         return `${day}-${month}-${year}`;
//       },
//     },
//     {
//       field: "yuvaEcEffectiveTo",
//       headerName: "Effective To",
//       align: "center",
//       width: 140,
//       valueFormatter: (params) => {
//         if (!params.value) return "";
//         const date = new Date(params.value);
//         const day = String(date.getDate()).padStart(2, "0");
//         const month = String(date.getMonth() + 1).padStart(2, "0");
//         const year = date.getFullYear();
//         return `${day}-${month}-${year}`;
//       },
//     },
//   ];

//   // Institute-specific columns (shown when activeTab is "institute")
//   const instituteColumns = [
//     {
//       field: "yuvaEcStatus",
//       headerName: "YUVA EC Status",
//       align: "center",
//       width: 150,
//       renderCell: renderYuvaECStatus,
//     },
//     {
//       field: "assignEC",
//       headerName: "Assign YUVA EC",
//       type: "actions",
//       width: 160,
//       align: "center",
//       renderCell: (params) => (
//         <button
//           className={css.editBtn}
//           style={{
//             backgroundColor: "#28a745",
//             border: "none",
//             color: "white",
//             padding: "6px 12px",
//             borderRadius: "4px",
//             fontSize: "12px",
//             cursor: "pointer",
//           }}
//           onClick={() => {
//             setSelectedStudent(params.row);
//             setShowEcModal(true);
//           }}
//         >
//           Assign YUVA EC
//         </button>
//       ),
//     },
//   ];

//   // Build columns array based on conditions
//   let columns = [...baseColumns];

//   // Add EC effective date columns when isEC is true
//   if (isECFromUrl === "true") {
//     columns = [...columns, ...ecColumns];
//   }

//   // Add institute-specific columns when activeTab is "institute"
//   if (activeTab === "institute") {
//     columns = [...columns, ...instituteColumns];
//   }

//   const rows = filteredUsers.map((user, idx) => ({ ...user, id: idx + 1 }));

//   // Subtle elevation and lift-on-hover for header cards
//   const ElevateOnHover = ({ children }) => {
//     const [hovered, setHovered] = useState(false);
//     return (
//       <div
//         onMouseEnter={() => setHovered(true)}
//         onMouseLeave={() => setHovered(false)}
//         style={{
//           borderRadius: 12,
//           background: "#ffffff",
//           transition: "box-shadow 200ms ease, transform 200ms ease",
//           boxShadow: hovered
//             ? "0 8px 24px rgba(16,24,40,.12)"
//             : "0 1px 3px rgba(16,24,40,.06)",
//           transform: hovered ? "translateY(-2px)" : "none",
//         }}
//       >
//         {children}
//       </div>
//     );
//   };

//   // Reusable stat card with icon and soft gradient
//   const StatCard = ({ title, value, icon, gradient }) => (
//     <ElevateOnHover>
//       <div className={css.dashboardCard} style={{ overflow: "hidden" }}>
//         <div
//           style={{
//             display: "flex",
//             justifyContent: "space-between",
//             alignItems: "center",
//             padding: "12px 16px",
//           }}
//         >
//           <h3
//             className={css.cardTitle}
//             style={{ margin: 0, fontSize: "14px", fontWeight: "600" }}
//           >
//             {title}
//           </h3>
//           <div
//             style={{
//               width: 36,
//               height: 36,
//               borderRadius: 10,
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "center",
//               background: gradient,
//               color: "#fff",
//             }}
//           >
//             <FontAwesomeIcon icon={icon} />
//           </div>
//         </div>
//         <div className={css.cardContent} style={{ paddingTop: 0 }}>
//           <div className={css.mainNumber}>{value}</div>
//         </div>
//       </div>
//     </ElevateOnHover>
//   );

//   function handleUsersClick() {
//     navigate(`/user/register/no_id?tab=${activeTab || "institute"}`);
//   }

//   // Your existing useEffect hooks remain the same
//   useEffect(() => {
//     async function getCollegeName() {
//       setIsLoading(true);
//       try {
//         const adminId = process.env.REACT_APP_ADMIN_ID;
//         const adminPassword = process.env.REACT_APP_ADMIN_PASSWORD;
//         const basicAuth = btoa(`${adminId}:${adminPassword}`);

//         const response = await fetch(
//           `${SERVER_ORIGIN}/api/admin/auth/users/college-names`,
//           {
//             method: "GET",
//             headers: {
//               "Content-Type": "application/json",
//               Authorization: `Basic ${basicAuth}`,
//               "auth-token": sessionStorage.getItem("token"),
//             },
//           }
//         );

//         const result = await response.json();
//         setIsLoading(false);

//         if (response.status >= 400 && response.status < 600) {
//           if (response.status === 401) {
//             navigate("/admin/login");
//           } else if (response.status === 500) {
//             toast.error(result.statusText);
//           }
//         }
//       } catch (err) {
//         setIsLoading(false);
//       }
//     }

//     getCollegeName();
//   }, [navigate]);

//   useEffect(() => {
//     setActiveTab(tabFromUrl);
//     async function getAllUsers() {
//       setIsLoading(true);
//       try {
//         const adminId = process.env.REACT_APP_ADMIN_ID;
//         const adminPassword = process.env.REACT_APP_ADMIN_PASSWORD;
//         const basicAuth = btoa(`${adminId}:${adminPassword}`);

//         const response = await fetch(
//           `${SERVER_ORIGIN}/api/admin/auth/users/all?page=${page}&limit=${limit}&search=${debouncedSearch}&sortBy=fName&sortType=${sortType === true ? "asc" : "desc"
//           }&collegeName=${searchCollege}&isThalir=${activeTab === "thalir" ? "true" : "false"
//           }&isEC=${isECFromUrl || "false"}`,
//           {
//             headers: {
//               method: "GET",
//               "Content-Type": "application/json",
//               Authorization: `Basic ${basicAuth}`,
//               "auth-token": sessionStorage.getItem("token"),
//             },
//           }
//         );

//         const result = await response.json();
//         setIsLoading(false);

//         if (response.status >= 400 && response.status < 600) {
//           if (response.status === 401) {
//             navigate("/admin/login");
//           } else if (response.status === 500) {
//             toast.error(result.statusText);
//           }
//         } else if (response.ok && response.status === 200) {
//           setAllUsers(result.filteredUsers);
//           setTotalPages(result.totalPages);
//         }
//       } catch (err) {
//         setIsLoading(false);
//       }
//     }

//     async function counting() {
//       setIsLoading(true);
//       try {
//         const response = await fetch(
//           `${SERVER_ORIGIN}/api/admin/auth/student-counting?isThalir=${activeTab === "thalir" ? "true" : "false"
//           }&isEC=${isECFromUrl || "false"}`,
//           {
//             method: "POST",
//             headers: {
//               "Content-Type": "application/json",
//               "auth-token": sessionStorage.getItem("token"),
//             },
//           }
//         );

//         const result = await response.json();
//         if (response.status >= 400 && response.status < 600) {
//           if (response.status === 401) {
//             navigate("/login?userType=institute");
//           }
//         } else if (response.ok && response.status === 200) {
//           SetAll_students(result.data.all_students);
//           SetAll_students_onboarding_pending(
//             result.data.all_students_onboarding_pending
//           );
//           SetAll_students_onboarding_invited(
//             result.data.all_students_onboarding_invited
//           );
//           SetAll_students_onboarding_onboarded(
//             result.data.all_students_onboarding_onboarded
//           );
//           SetAll_students_email_sent(result.data.all_students_email_sent);
//           SetAll_students_email_not_sent(
//             result.data.all_students_email_not_sent
//           );
//         } else {
//           navigate("/login?userType=institute");
//         }
//       } catch (err) {
//         // Handle error
//       }
//       setIsLoading(false);
//     }

//     counting();
//     getAllUsers();
//   }, [
//     page,
//     sortType,
//     debouncedSearch,
//     searchCollege,
//     navigate,
//     limit,
//     activeTab,
//   ]);

//   function CustomColumnMenu(props) {
//     return (
//       <GridColumnMenu
//         {...props}
//         slotProps={{
//           columnMenuUserItem: {
//             displayOrder: 15,
//             myCustomValue: "Do custom action",
//             myCustomHandler: () => alert("Custom handler fired"),
//           },
//         }}
//       />
//     );
//   }

//   return (
//     <div className={css.outerDiv}>
//       {/* Header Section */}
//       <div className={css.headerSection}>
//         <div className={css.headerLeft}>
//           <BackButton
//             text="Back to Dashboard"
//             customPath={getDashboardPath()}
//           />
//         </div>
//       </div>

//       {isReadOnly && (
//         <div className={css.readOnlyAlert}>
//           <div className="alert alert-info" role="alert">
//             <strong>Read-Only Access:</strong> You can view all student data but
//             cannot add, edit, or upload new students.
//           </div>
//         </div>
//       )}

//       <div className="my-4">
//         <SecCard>
//           <Row className="g-4">
//             <div
//               className={`${css.serviceHeader} d-flex justify-content-between align-items-center mb-3`}
//             >
//               <div className={css.serviceName}>Students Data</div>

//               <div className={css.headerRightSection}>
//                 {!isReadOnly && (
//                   <div className={css.actionButtons}>
//                     <button className={css.addBtn} onClick={handleUsersClick}>
//                       <FontAwesomeIcon icon={faPlus} className={css.addIcon} />
//                       Add Student
//                     </button>

//                     <ActionsDropdown
//                       onDownload={() =>
//                         document
//                           .querySelector(`.${css.downloadExcelBtn}`)
//                           ?.click()
//                       }
//                       onUpload={() =>
//                         document.querySelector(`.${css.fileUploadBtn}`)?.click()
//                       }
//                     />

//                     {/* Hidden triggers for download and upload */}
//                     <div style={{ display: "none" }}>
//                       <DownloadExcel
//                         ref={downloadExcelRef}
//                         className={css.downloadExcelBtn}
//                       />
//                       <FileUpload
//                         ref={fileUploadRef}
//                         collegeName={selectedOption}
//                         className={css.fileUploadBtn}
//                       />
//                     </div>
//                   </div>
//                 )}
//               </div>
//             </div>

//             {/* UPLOAD AND DOWNLOAD CONTROLS - ADDED FROM INSTITUTE */}
//             <div className={css.uploadDownloadSection}>
//               <div className="row align-items-center">
//                 <div className="col-md-4">
//                   <div style={{ marginBottom: "1rem" }}>
//                     <AsyncSelect
//                       cacheOptions
//                       loadOptions={loadOptions}
//                       defaultOptions={defaultOptions}
//                       onChange={(selectedOption) => {
//                         if (selectedOption) {
//                           setRegisForm(prevForm => ({
//                             ...prevForm,
//                             collegeName: selectedOption.label,
//                           }));
//                           setSelectedOption(selectedOption);
//                         }
//                       }}
//                       value={selectedOption}
//                       placeholder="Select / Search Institute"
//                     />
//                   </div>
//                 </div>
//                 <div className="col-md-8">
//                   <div className="d-flex justify-content-end gap-3 align-items-center">
//                     <DownloadExcel />
//                     <FileUpload collegeName={selectedOption} />
//                   </div>
//                 </div>
//               </div>
//             </div>

//             <Col md={6} lg={6} sm={12} xs={12}>
//               <StatCard
//                 title={
//                   activeTab === "institute"
//                     ? "YUVA Students"
//                     : "Thalir Students"
//                 }
//                 value={all_students}
//                 icon={faUserGraduate}
//                 gradient="linear-gradient(135deg,#6366f1,#8b5cf6)"
//               />
//             </Col>

//             <Col md={6} lg={6} sm={12} xs={12}>
//               <StatCard
//                 title="Onboarding Status"
//                 value={
//                   <div
//                     style={{
//                       display: "flex",
//                       justifyContent: "space-between",
//                       gap: "12px",
//                     }}
//                   >
//                     <div
//                       style={{
//                         display: "flex",
//                         flexDirection: "column",
//                         alignItems: "center",
//                         flex: 1,
//                       }}
//                     >
//                       <span
//                         style={{
//                           fontSize: "12px",
//                           color: "#6b7280",
//                           fontWeight: "500",
//                           marginBottom: "4px",
//                         }}
//                       >
//                         Total
//                       </span>
//                       <span
//                         style={{
//                           fontSize: "18px",
//                           color: "#1f2937",
//                           fontWeight: "600",
//                         }}
//                       >
//                         {all_students_onboarding_onboarded +
//                           all_students_onboarding_invited +
//                           all_students_onboarding_pending}
//                       </span>
//                     </div>
//                     <div
//                       style={{
//                         display: "flex",
//                         flexDirection: "column",
//                         alignItems: "center",
//                         flex: 1,
//                       }}
//                     >
//                       <span
//                         style={{
//                           fontSize: "12px",
//                           color: "#6b7280",
//                           fontWeight: "500",
//                           marginBottom: "4px",
//                         }}
//                       >
//                         Onboarded
//                       </span>
//                       <span
//                         style={{
//                           fontSize: "18px",
//                           color: "#1f2937",
//                           fontWeight: "600",
//                         }}
//                       >
//                         {all_students_onboarding_onboarded}
//                       </span>
//                     </div>
//                     <div
//                       style={{
//                         display: "flex",
//                         flexDirection: "column",
//                         alignItems: "center",
//                         flex: 1,
//                       }}
//                     >
//                       <span
//                         style={{
//                           fontSize: "12px",
//                           color: "#6b7280",
//                           fontWeight: "500",
//                           marginBottom: "4px",
//                         }}
//                       >
//                         Invited
//                       </span>
//                       <span
//                         style={{
//                           fontSize: "18px",
//                           color: "#1f2937",
//                           fontWeight: "600",
//                         }}
//                       >
//                         {all_students_onboarding_invited}
//                       </span>
//                     </div>
//                     <div
//                       style={{
//                         display: "flex",
//                         flexDirection: "column",
//                         alignItems: "center",
//                         flex: 1,
//                       }}
//                     >
//                       <span
//                         style={{
//                           fontSize: "12px",
//                           color: "#6b7280",
//                           fontWeight: "500",
//                           marginBottom: "4px",
//                         }}
//                       >
//                         Pending
//                       </span>
//                       <span
//                         style={{
//                           fontSize: "18px",
//                           color: "#1f2937",
//                           fontWeight: "600",
//                         }}
//                       >
//                         {all_students_onboarding_pending}
//                       </span>
//                     </div>
//                   </div>
//                 }
//                 icon={faUserCheck}
//                 gradient="linear-gradient(135deg,#f59e0b,#f97316)"
//               />
//             </Col>
//           </Row>
//         </SecCard>
//       </div>

//       {/* Search and Filters Section */}
//       <div className={css.searchFiltersSection}>
//         <div className={css.filtersContainer}>
//           <div className={css.filterGroup}>
//             <label className={css.filterLabel}>Search</label>
//             <div className={css.searchInputWrapper}>
//               <input
//                 type="text"
//                 value={searchQuery}
//                 placeholder="Search from the list"
//                 onChange={(e) => setSearchQuery(e.target.value)}
//                 className={css.searchInput}
//               />
//             </div>
//           </div>

//           <div className={css.filterGroup}>
//             <label className={css.filterLabel}>Region</label>
//             <select
//               value={filters.region}
//               onChange={(e) => handleFilterChange("region", e.target.value)}
//               className={css.filterSelect}
//             >
//               <option value="">All Regions</option>
//               {uniqueRegions.map((region) => (
//                 <option key={region} value={region}>
//                   {region}
//                 </option>
//               ))}
//             </select>
//           </div>

//           <div className={css.filterGroup}>
//             <label className={css.filterLabel}>College</label>
//             <select
//               value={filters.collegeName}
//               onChange={(e) =>
//                 handleFilterChange("collegeName", e.target.value)
//               }
//               className={css.filterSelect}
//             >
//               <option value="">All Colleges</option>
//               {uniqueColleges.map((college) => (
//                 <option key={college} value={college}>
//                   {college}
//                 </option>
//               ))}
//             </select>
//           </div>

//           <button onClick={clearAllFilters} className={css.clearFiltersBtn}>
//             Clear All
//           </button>
//         </div>
//       </div>

//       {/* Data Grid Section */}
//       {isLoading ? (
//         <Loader />
//       ) : (
//         <div className={css.dataGridContainer}>
//           <DataGrid
//             rows={rows}
//             columns={columns}
//             autoHeight
//             density="compact"
//             initialState={{
//               pagination: {
//                 paginationModel: { pageSize: 10, page: 0 },
//               },
//             }}
//             pageSizeOptions={[10, 25, 50]}
//             disableRowSelectionOnClick
//             sx={{
//               borderRadius: 2,
//               border: "1px solid #e5e7eb",
//               "& .MuiDataGrid-columnHeaders": {
//                 backgroundColor: "#f9fafb",
//                 color: "#111827",
//                 fontWeight: 600,
//               },
//               "& .MuiDataGrid-row:nth-of-type(odd)": {
//                 backgroundColor: "#fcfcfd",
//               },
//               "& .MuiDataGrid-row:hover": {
//                 backgroundColor: "#eef2ff",
//               },
//             }}
//             slots={{ columnMenu: CustomColumnMenu, toolbar: GridToolbar }}
//             slotProps={{
//               toolbar: {
//                 showQuickFilter: true,
//                 quickFilterProps: { debounceMs: 300 },
//               },
//             }}
//             loading={isLoading}
//           />
//         </div>
//       )}

//       <YuvaEcAssignmentModal
//         show={showEcModal}
//         onHide={() => setShowEcModal(false)}
//         student={selectedStudent}
//         onSuccess={() => {
//           setRerender(!rerender);
//         }}
//       />
//     </div>
//   );
// };

// const ActionsDropdown = ({ onDownload, onUpload }) => {
//   const [isOpen, setIsOpen] = useState(false);

//   const toggleDropdown = () => setIsOpen(!isOpen);

//   // Close dropdown when clicking outside
//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (!event.target.closest(`.${css.dropdownContainer}`)) {
//         setIsOpen(false);
//       }
//     };

//     document.addEventListener('mousedown', handleClickOutside);
//     return () => document.removeEventListener('mousedown', handleClickOutside);
//   }, []);

//   return (
//     <div className={css.dropdownContainer}>
//       <button
//         className={css.ellipsisBtn}
//         onClick={toggleDropdown}
//         aria-label="More actions"
//       >
//         <FontAwesomeIcon icon={faEllipsisVertical} />
//       </button>

//       {isOpen && (
//         <div className={css.dropdownMenu}>
//           <button
//             className={css.dropdownItem}
//             onClick={() => {
//               onDownload?.();
//               setIsOpen(false);
//             }}
//           >
//             <FontAwesomeIcon icon={faDownload} className={css.dropdownIcon} />
//             Download Excel
//           </button>
//           <button
//             className={css.dropdownItem}
//             onClick={() => {
//               onUpload?.();
//               setIsOpen(false);
//             }}
//           >
//             <FontAwesomeIcon icon={faUpload} className={css.dropdownIcon} />
//             Upload Students
//           </button>
//         </div>
//       )}
//     </div>
//   );
// };

// export default AdminUsers;